'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Image from 'next/image';

export default function HowItWorks() {
  const { t } = useTranslation('home');

  const steps = [
    {
      number: '01',
      title: t('howItWorks.steps.step1.title'),
      description: t('howItWorks.steps.step1.description'),
      icon: '💧',
      color: 'bg-blue-pale border-blue-brand/20',
      numColor: 'text-blue-brand',
      img: '/assets/steps/step1.jpeg',
    },
    {
      number: '02',
      title: t('howItWorks.steps.step2.title'),
      description: t('howItWorks.steps.step2.description'),
      icon: '🧴',
      color: 'bg-primary-50 border-primary-100',
      numColor: 'text-primary',
      img: '/assets/steps/step2.jpeg',
    },
    {
      number: '03',
      title: t('howItWorks.steps.step3.title'),
      description: t('howItWorks.steps.step3.description'),
      icon: '⏱️',
      color: 'bg-amber-50 border-amber-100',
      numColor: 'text-gold-dark',
      img: '/assets/steps/step3.jpeg',
    },
    {
      number: '04',
      title: t('howItWorks.steps.step4.title'),
      description: t('howItWorks.steps.step4.description'),
      icon: '✅',
      color: 'bg-primary-50 border-primary-100',
      numColor: 'text-primary',
      img: '/assets/steps/step4.jpeg',
    },
  ];

  const handleBuyNow = () => {
    document.getElementById('product')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Animation constants
  const VIEWPORT_CONFIG = { once: true, margin: '-100px' };
  const TIMELINE_DURATION = 1.2;

  return (
    <section
      id="how-it-works"
      className="py-16 sm:py-32 lg:py-16 bg-white relative overflow-hidden"
      aria-labelledby="how-heading"
    >
      {/* Background decoration */}
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-5 pointer-events-none"
        style={{ background: 'radial-gradient(circle, var(--color-primary) 0%, transparent 70%)' }}
        aria-hidden="true"
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* ── Header ──────────────────────────────────────── */}
        <div className="grid grid-cols-12 gap-4 sm:gap-8 mb-12">
          <motion.div
            className="col-span-12 lg:col-span-6 min-w-0"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT_CONFIG}
            transition={{ duration: 0.8 }}
          >
            <span className="text-xs font-semibold tracking-widest uppercase text-muted mb-4 block">
              {t('howItWorks.label')}
            </span>
            <h2
              id="how-heading"
              className="section-headline font-display font-light text-foreground leading-snug"
            >
              {t('howItWorks.title.line1')}{' '}
              <span className="inline-block italic pl-0.5 pr-2 text-gradient-green">
                {t('howItWorks.title.highlight')}
              </span>
              <br />
              {t('howItWorks.title.line2')}
            </h2>
          </motion.div>

          <motion.div
            className="col-span-12 lg:col-span-5 lg:col-start-8 min-w-0 lg:flex lg:items-end"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT_CONFIG}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="text-muted text-lg leading-relaxed font-light break-words max-w-full">
              {t('howItWorks.description')}
            </p>
          </motion.div>
        </div>

        {/* ── Steps ─────────────────────────────────────── */}
        <div className="relative">
          {/* DESKTOP: horizontal animated line track */}
          <div
            className="hidden lg:block absolute left-0 right-0 h-[2px] bg-gray-100 pointer-events-none"
            style={{ top: '3.5rem' }}
            aria-hidden="true"
          >
            {/* Filled portion */}
            <motion.div
              className="absolute top-0 left-0 h-full"
              style={{ background: 'var(--color-primary, #16a34a)' }}
              initial={{ width: '0%' }}
              whileInView={{ width: '100%' }}
              viewport={VIEWPORT_CONFIG}
              transition={{ duration: TIMELINE_DURATION, ease: 'easeInOut' }}
            />

            {/* Travelling dot */}
            <motion.div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full -translate-x-1/2"
              style={{
                background: 'var(--color-primary, #16a34a)',
                boxShadow: '0 0 0 4px rgba(22,163,74,0.15)',
              }}
              initial={{ left: '0%' }}
              whileInView={{ left: '100%' }}
              viewport={VIEWPORT_CONFIG}
              transition={{ duration: TIMELINE_DURATION, ease: 'easeInOut' }}
            />
          </div>

          {/* MOBILE: vertical animated line track */}
          <div
            className="lg:hidden absolute left-1/2 top-0 bottom-0 w-[2px] bg-gray-100 pointer-events-none"
            aria-hidden="true"
          >
            {/* Filled portion */}
            <motion.div
              className="absolute top-0 left-0 w-full"
              style={{ background: 'var(--color-primary, #16a34a)' }}
              initial={{ height: '0%' }}
              whileInView={{ height: '100%' }}
              viewport={VIEWPORT_CONFIG}
              transition={{ duration: TIMELINE_DURATION, ease: 'easeInOut' }}
            />

            {/* Travelling dot */}
            <motion.div
              className="absolute left-1/2 -translate-x-1/2 w-3 h-3 rounded-full -translate-y-4"
              style={{
                background: 'var(--color-primary, #16a34a)',
                boxShadow: '0 0 0 4px rgba(22,163,74,0.15)',
              }}
              initial={{ top: '0%' }}
              whileInView={{ top: '100%' }}
              viewport={VIEWPORT_CONFIG}
              transition={{ duration: TIMELINE_DURATION, ease: 'easeInOut' }}
            />
          </div>

          {/* Cards grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 min-h-fit gap-8">
            {steps.map((step, i) => {
              // Calculate a slight stagger delay for each card so they gracefully cascade in
              const cardDelay = i * 0.2;

              return (
                <div key={step.number} className="relative min-h-fit">
                  {/* Desktop circle badge */}
                  <motion.div
                    className={`hidden sm:flex relative z-10 w-14 h-14 rounded-full ${step.color} border-2 items-center justify-center mb-8 shadow-soft`}
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={VIEWPORT_CONFIG}
                    transition={{
                      type: 'spring',
                      stiffness: 380,
                      damping: 16,
                      delay: cardDelay,
                    }}
                  >
                    <span className={`font-display font-semibold text-sm ${step.numColor}`}>
                      {step.number}
                    </span>
                  </motion.div>

                  {/* Card */}
                  {/* Card */}
                  <motion.div
                    className={`${step.color} border rounded-4xl h-full sm:h-auto flex flex-col trust-badge relative overflow-hidden`}
                    initial={{ opacity: 0, y: 28, scale: 0.95 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={VIEWPORT_CONFIG}
                    transition={{
                      duration: 0.5,
                      ease: [0.16, 1, 0.3, 1],
                      delay: cardDelay + 0.1,
                    }}
                    whileHover={{ y: -6, transition: { duration: 0.3 } }}
                  >
                    {/* Text Wrapper - sm:flex-none removes the stretching gap on desktop */}
                    <div className="p-6 sm:p-8 flex-1 sm:flex-none">
                      <span className="text-4xl mb-5 block">{step.icon}</span>
                      <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                        {step.title}
                      </h3>
                      <p className="text-muted text-sm leading-relaxed pb-6 sm:pb-0">
                        {step.description}
                      </p>
                    </div>

                    {/* Image Wrapper */}
                    <div className="w-full h-[200px] sm:h-[250px] shrink-0 ">
                      <Image
                        src={step.img}
                        alt={`Illustration for step ${step.number}`}
                        width={250}
                        height={250}
                        className="w-full h-full object-cover rounded-b-4xl"
                      />
                    </div>

                    {/* Mobile step badge */}
                    <div
                      className={`sm:hidden absolute bottom-0 left-0 w-16 h-14 ${step.color} border-t border-r flex items-center justify-center rounded-tr-2xl shadow-soft z-10`}
                    >
                      <span className={`font-display font-semibold text-sm ${step.numColor}`}>
                        {step.number}
                      </span>
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── CTA ─────────────────────────────────────────── */}
        <motion.div
          className="mt-20 sm:mt-28 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={VIEWPORT_CONFIG}
          transition={{ duration: 0.7, delay: 0.6 }} // Delays enough to load after cards
        >
          <p className="font-display text-2xl lg:text-3xl italic font-light text-foreground my-4">
            {t('howItWorks.ctaText')}
          </p>
          <button
            onClick={handleBuyNow}
            className="btn-primary w-full sm:w-auto px-12 py-5 rounded-2xl font-semibold text-base tracking-wide shadow-green-lg"
            aria-label="Buy Dr. Detoxer and start cleaning produce safely"
          >
            {t('hero.buyNowPrice')}
          </button>
        </motion.div>
      </div>
    </section>
  );
}